import { Box, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';

import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { changeCounterAccount } from '../../../store/positions/invoice-detail-positions-actions';
import { counterAccountChangeSelector } from '../../../store/positions/invoice-detail-positions-selectors';
import { interchangeableTaxRates } from '../../../utils/interchangeable-tax-rates';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

export interface SentInvoiceCounterAccountProps {
    name: string;
    itemId?: number;
    taxRate?: TaxRate | null;
    taxRates: TaxRate[];
}

/**
 * The account cell of a line on a sent invoice: changeable until the invoice
 * went to BMD, to an interchangeable account only; afterwards it says when.
 */
export const SentInvoiceCounterAccount = ({ name, itemId, taxRate, taxRates }: SentInvoiceCounterAccountProps) => {
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const { changeable, exportedAt } = useAppSelector(counterAccountChangeSelector);
    const options = useMemo(() => (taxRate ? interchangeableTaxRates(taxRate, taxRates) : []), [taxRate, taxRates]);
    const canChange = changeable && undefined !== itemId && options.length > 1;

    const field = (
        <TaxRateAutocomplete
            display="short"
            name={name}
            disabled={!canChange}
            taxRates={canChange ? options : taxRates}
            onValueChange={chosen => {
                if (chosen && undefined !== itemId && chosen.id !== taxRate?.id) {
                    dispatch(changeCounterAccount(itemId, chosen.id));
                }
            }}
            showLabel={false}
            textAlign="right"
        />
    );

    if (!exportedAt) {
        return field;
    }

    return (
        <Tooltip
            title={intl.formatMessage(
                { id: 'app.invoices.positions.counterAccountExported' },
                { date: intl.formatDate(exportedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }) }
            )}
            placement="top"
        >
            <Box>{field}</Box>
        </Tooltip>
    );
};
